import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "./searchable-select";
import { FormDialog, Field } from "./form-dialog";
import { invoices } from "@/mocks/data";
import { invoiceNumber, lookupValue, isoDate, isoDateNotFuture } from "@/lib/validators";

function nextInvoiceNumber() {
  const max = invoices
    .map((i) => parseInt(i.number.replace(/\D/g, ""), 10))
    .filter((n) => Number.isFinite(n))
    .reduce((a, b) => Math.max(a, b), 0);
  return `INV-${String(max + 1).padStart(4, "0")}`;
}

const schema = z
  .object({
    number: invoiceNumber,
    client: lookupValue("Pick a client"),
    amount: z.coerce.number().min(1, "Min $1").max(10_000_000, "Max $10,000,000"),
    issued: isoDateNotFuture,
    due: isoDate,
    status: z.enum(["draft", "sent", "paid", "overdue"]),
  })
  .refine((v) => v.due >= v.issued, {
    message: "Due date must be on or after issued",
    path: ["due"],
  });
type Values = z.infer<typeof schema>;

export function NewInvoiceDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      number: nextInvoiceNumber(), client: "", amount: 0,
      issued: "", due: "", status: "draft",
    },
  });
  const [clientLabel, setClientLabel] = useState("");
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> New invoice</Button>}
      title="Create an invoice"
      description="Issue a new invoice. Mark it sent once delivered to the client."
      submitLabel="Create invoice"
      isDirty={isDirty}
      onReset={() => { reset({ ...watch(), number: nextInvoiceNumber() }); setClientLabel(""); }}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Invoice created", { description: `${v.number} · ${v.client}` });
          resolve();
        }, () => reject())();
      })}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Invoice #" htmlFor="iv-num" error={errors.number?.message}>
          <Input id="iv-num" {...register("number")} />
        </Field>
        <Field label="Status" error={errors.status?.message}>
          <Select value={watch("status")} onValueChange={(v) => setValue("status", v as Values["status"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label="Client" error={errors.client?.message}>
        <SearchableSelect
          resource="clients"
          value={watch("client")}
          selectedLabel={clientLabel}
          onChange={(v, label) => {
            setValue("client", v, { shouldValidate: true, shouldDirty: true });
            setClientLabel(label);
          }}
          placeholder="Select client"
          searchPlaceholder="Search clients…"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Amount (USD)" htmlFor="iv-amt" error={errors.amount?.message}>
          <Input id="iv-amt" type="number" min={0} step={100} {...register("amount")} />
        </Field>
        <Field label="Issued" htmlFor="iv-iss" error={errors.issued?.message}>
          <Input id="iv-iss" type="date" {...register("issued")} />
        </Field>
        <Field label="Due" htmlFor="iv-due" error={errors.due?.message}>
          <Input id="iv-due" type="date" {...register("due")} />
        </Field>
      </div>
    </FormDialog>
  );
}
